import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const MobileMenuContext = createContext(null);

/** Clear leftover body locks from Radix dialogs that can block the menu button. */
export const clearBodyInteractionLocks = () => {
  if (typeof document === 'undefined') return;
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  document.body.removeAttribute('data-scroll-locked');
  // Radix sometimes sets this on <html> as well
  document.documentElement.style.pointerEvents = '';
  document.documentElement.removeAttribute('data-scroll-locked');
};

export const MobileMenuProvider = ({ children }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const openMenu = useCallback(() => {
    clearBodyInteractionLocks();
    setIsMobileOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMobileOpen(false);
    // Restore scroll after drawer closes
    clearBodyInteractionLocks();
  }, []);

  const toggleMenu = useCallback(() => {
    clearBodyInteractionLocks();
    setIsMobileOpen((open) => !open);
  }, []);

  const value = useMemo(
    () => ({ isMobileOpen, openMenu, closeMenu, toggleMenu, setIsMobileOpen }),
    [isMobileOpen, openMenu, closeMenu, toggleMenu]
  );

  return (
    <MobileMenuContext.Provider value={value}>
      {children}
    </MobileMenuContext.Provider>
  );
};

export const useMobileMenu = () => {
  const ctx = useContext(MobileMenuContext);
  if (!ctx) {
    // Safe no-op fallback — callers should still be under AppLayout provider
    return {
      isMobileOpen: false,
      openMenu: clearBodyInteractionLocks,
      closeMenu: clearBodyInteractionLocks,
      toggleMenu: clearBodyInteractionLocks,
      setIsMobileOpen: () => {},
    };
  }
  return ctx;
};
